import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, InputLabel, LinearProgress, MenuItem, Radio, RadioGroup, Select, Stack,
    Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import EnergyApiService from '../../api/EnergyApiService';
import SaveButton from '../Common/SaveButton';
import { useSnackbar } from '../Common/SnackbarProvider';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import {
    ANOMALY_TOLERANCE, DEFAULT_OUTPUT_UNIT, ISO_DATE, OUTPUT_UNIT_SUGGESTIONS, adjacentPeriod, formatQuantity,
    medianRate, parseQuantity, periodDays, rateDeviation,
} from './energyEntryUtils';

const energyApiService = new EnergyApiService();

/** Matches the server's longest allowed period. */
const MAX_PERIOD_DAYS = 400;

/** How far back the wizard looks for the last period, to suggest the next one. */
const HISTORY_YEARS = 3;

const BILL_STEPS = ['meter', 'period', 'amount', 'review'];
const PRODUCTION_STEPS = ['period', 'amount', 'review'];

const NO_ROWS = [];

const blankAmounts = { amount: '', sold: '', isEstimated: false, statementDate: '', notes: '' };

const sortByStart = rows => rows.slice().sort((a, b) => a.periodStart.localeCompare(b.periodStart));

/**
 * One utility bill or one production figure, entered a step at a time. The period is suggested
 * from the last one saved, so most entries are a click through to the amount and a save. After
 * saving, the next period can be entered straight away.
 */
export default function EntryWizard ({ open, onClose, kind, projectId, siteId, meters = NO_ROWS, units = NO_ROWS, initialMeterId, onSaved }) {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const isBill = kind === 'bill';
    const steps = isBill ? BILL_STEPS : PRODUCTION_STEPS;

    const billedMeters = useMemo(() => meters.filter(meter => meter.resolution === 'BilledOnly'), [meters]);

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(0);
    const [saved, setSaved] = useState(null);

    const [meterId, setMeterId] = useState(null);
    const [locationWide, setLocationWide] = useState(false);
    const [period, setPeriod] = useState({ periodStart: '', periodEnd: '' });
    const [unitId, setUnitId] = useState(null);
    const [outputUnit, setOutputUnit] = useState(DEFAULT_OUTPUT_UNIT);
    const [values, setValues] = useState(blankAmounts);

    const meter = billedMeters.find(m => m.id === meterId) ?? null;

    // The saved periods the new one follows on from and must not overlap.
    const ownHistory = useMemo(() => sortByStart(isBill
        ? history.filter(row => row.meterId === meterId)
        : history.filter(row => row.energySiteId === (locationWide ? null : (siteId ?? null)))), [history, isBill, meterId, locationWide, siteId]);

    const suggest = rows => {
        const fallbackStart = dayjs().subtract(1, 'month').startOf('month').format(ISO_DATE);
        setPeriod(adjacentPeriod(rows, 'after', { fallbackStart, monthly: !isBill }));
    };

    useEffect(() => {
        if (!open) return;

        setStep(0);
        setSaved(null);
        setValues(blankAmounts);
        setLocationWide(false);

        const preset = billedMeters.find(m => m.id === initialMeterId) ?? (billedMeters.length === 1 ? billedMeters[0] : null);
        setMeterId(preset?.id ?? null);
        setUnitId(preset?.defaultUnitId ?? null);

        // With only one meter there is nothing to choose; start on the period.
        if (isBill && preset && billedMeters.length === 1) setStep(1);

        async function loadHistory () {
            setLoading(true);
            try {
                const from = dayjs().subtract(HISTORY_YEARS, 'year').format(ISO_DATE);
                const to = dayjs().add(1, 'year').format(ISO_DATE);
                const response = isBill
                    ? await energyApiService.getBills(siteId, from, to)
                    : await energyApiService.getProduction(projectId, from, to, siteId);

                if (!response?.success) {
                    snackbar.error(response?.message ?? t('siteData.toast.loadFailed'));
                    setHistory([]);
                    return;
                }

                setHistory(response.data);
                if (!isBill) {
                    const latest = sortByStart(response.data).pop();
                    if (latest?.outputUnit) setOutputUnit(latest.outputUnit);
                }
            } finally {
                setLoading(false);
            }
        }

        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // A new meter or scope means a different last period to follow on from.
    useEffect(() => {
        if (open && !loading) suggest(ownHistory);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loading, meterId, locationWide]);

    const chooseMeter = id => {
        const chosen = billedMeters.find(m => m.id === id);
        setMeterId(id);
        setUnitId(chosen?.defaultUnitId ?? null);
    };

    const setValue = (field, value) => setValues(current => ({ ...current, [field]: value }));

    const unitOptions = useMemo(
        () => (meter ? units.filter(unit => unit.commodity === meter.commodity || unit.commodity === 'Other') : units),
        [units, meter]);

    const unitCode = isBill ? (units.find(unit => unit.id === unitId)?.code ?? meter?.defaultUnitCode ?? '') : outputUnit;
    const lastPeriod = ownHistory[ownHistory.length - 1] ?? null;
    const days = periodDays(period.periodStart, period.periodEnd);

    const periodError = useMemo(() => {
        if (!period.periodStart || !period.periodEnd) return t('siteData.wizard.error.periodRequired');
        if (period.periodEnd < period.periodStart) return t('siteData.issue.outOfOrder');
        if (days > MAX_PERIOD_DAYS) return t('siteData.wizard.error.periodTooLong', { days: MAX_PERIOD_DAYS });

        const clash = ownHistory.find(row => row.periodStart <= period.periodEnd && row.periodEnd >= period.periodStart);
        return clash ? t('siteData.wizard.error.overlap', { start: clash.periodStart, end: clash.periodEnd }) : null;
    }, [period, days, ownHistory, t]);

    const amount = parseQuantity(values.amount);
    const sold = parseQuantity(values.sold);

    const amountError = useMemo(() => {
        const bad = value => Number.isNaN(value) || (value !== null && value < 0);
        if (isBill) {
            if (amount === null) return t('siteData.wizard.error.amountRequired');
            return bad(amount) ? t('siteData.wizard.error.amountInvalid') : null;
        }

        if (amount === null && sold === null) return t('siteData.wizard.error.productionRequired');
        if (bad(amount) || bad(sold)) return t('siteData.wizard.error.amountInvalid');
        return outputUnit.trim() ? null : t('siteData.wizard.error.unitRequired');
    }, [isBill, amount, sold, outputUnit, t]);

    const stepError = {
        meter: meter ? null : t('siteData.wizard.error.meterRequired'),
        period: periodError,
        amount: amountError,
        review: null,
    }[steps[step]];

    // How the new figure compares with this meter's or site's usual daily rate.
    const deviation = useMemo(() => {
        const field = isBill ? 'consumption' : 'quantity';
        const median = medianRate(ownHistory.map(row => ({ ...row, consumption: row[field] })));
        return rateDeviation({ ...period, value: values.amount }, median, 'value');
    }, [ownHistory, period, values.amount, isBill]);

    const unusual = deviation !== null && Math.abs(deviation) > ANOMALY_TOLERANCE;

    const next = () => {
        if (!stepError) setStep(current => Math.min(current + 1, steps.length - 1));
    };

    const back = () => setStep(current => Math.max(current - 1, 0));

    const nextOnEnter = event => {
        if (event.key === 'Enter') {
            event.preventDefault();
            next();
        }
    };

    const { run: save, loading: saving } = useAsyncCallback(async () => {
        const response = isBill
            ? await energyApiService.saveBills(meterId, [{
                id: null,
                periodStart: period.periodStart,
                periodEnd: period.periodEnd,
                consumption: amount,
                unitId,
                isEstimated: values.isEstimated,
                statementDate: values.statementDate || null,
            }])
            : await energyApiService.saveProduction(projectId, [{
                id: null,
                energySiteId: locationWide ? null : (siteId ?? null),
                periodStart: period.periodStart,
                periodEnd: period.periodEnd,
                quantity: amount,
                soldQuantity: sold,
                outputUnit: outputUnit.trim(),
                notes: values.notes || null,
            }]);

        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.saveFailed'));
            return;
        }

        const record = response.data.find(row =>
            row.periodStart === period.periodStart && row.periodEnd === period.periodEnd
            && (isBill || row.energySiteId === (locationWide ? null : (siteId ?? null))));

        if (record) {
            setHistory(current => [...current, record]);
            onSaved(record);
        }

        setSaved(record ?? { ...period });
    });

    // Straight on to the period after the one just saved, keeping the meter and unit.
    const enterNext = () => {
        setSaved(null);
        setValues(blankAmounts);
        setStep(steps.indexOf('period'));
        suggest(ownHistory);
    };

    const followsOn = lastPeriod && period.periodStart === dayjs(lastPeriod.periodEnd).add(1, 'day').format(ISO_DATE);

    const datePicker = (field, label) => (
        <DesktopDatePicker
            label={label}
            inputFormat="YYYY-MM-DD"
            value={period[field] ? dayjs(period[field]) : null}
            onChange={value => setPeriod(current => ({ ...current, [field]: value?.isValid() ? value.format(ISO_DATE) : '' }))}
            renderInput={params => <TextField {...params} size="small" />}
        />
    );

    const renderStep = () => {
        switch (steps[step]) {
            case 'meter':
                return (
                    <RadioGroup value={meterId ?? ''} onChange={event => chooseMeter(Number(event.target.value))}>
                        {billedMeters.map(option => {
                            const last = sortByStart(history.filter(row => row.meterId === option.id)).pop();
                            return (
                                <FormControlLabel
                                    key={option.id}
                                    value={option.id}
                                    control={<Radio />}
                                    label={(
                                        <Box>
                                            <Typography variant="body2">
                                                {t(`siteData.commodity.${option.commodity}`)} · {option.utilityName} · {option.accountNumber}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {last ? t('siteData.wizard.lastBill', { end: last.periodEnd }) : t('siteData.wizard.noBills')}
                                            </Typography>
                                        </Box>
                                    )}
                                    sx={{ py: 0.5 }}
                                />
                            );
                        })}
                    </RadioGroup>
                );

            case 'period':
                return (
                    <Stack spacing={2}>
                        {!isBill && (
                            <FormControlLabel
                                control={<Checkbox checked={locationWide} onChange={event => setLocationWide(event.target.checked)} />}
                                label={t('siteData.wizard.locationWide')}
                            />
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                            {datePicker('periodStart', t('siteData.column.periodStart'))}
                            {datePicker('periodEnd', t('siteData.column.periodEnd'))}
                            <Typography variant="body2" color="text.secondary">
                                {days ? t('siteData.wizard.days', { count: days }) : ''}
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {followsOn
                                ? t(isBill ? 'siteData.wizard.followsBill' : 'siteData.wizard.followsProduction', { end: lastPeriod.periodEnd })
                                : t(isBill ? 'siteData.wizard.checkBillDates' : 'siteData.wizard.checkProductionDates')}
                        </Typography>
                        {periodError && period.periodStart && period.periodEnd && <Alert severity="warning">{periodError}</Alert>}
                    </Stack>
                );

            case 'amount':
                return isBill
                    ? (
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    autoFocus
                                    label={t('siteData.column.consumption')}
                                    size="small"
                                    value={values.amount}
                                    error={Number.isNaN(amount)}
                                    inputProps={{ inputMode: 'decimal' }}
                                    onChange={event => setValue('amount', event.target.value)}
                                    onKeyDown={nextOnEnter}
                                />
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel id="wizard-unit-label">{t('siteData.column.unit')}</InputLabel>
                                    <Select
                                        labelId="wizard-unit-label"
                                        label={t('siteData.column.unit')}
                                        value={unitId ?? ''}
                                        onChange={event => setUnitId(event.target.value)}
                                    >
                                        {unitOptions.map(unit => <MenuItem key={unit.id} value={unit.id}>{unit.code}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                            <FormControlLabel
                                control={<Checkbox checked={values.isEstimated} onChange={event => setValue('isEstimated', event.target.checked)} />}
                                label={t('siteData.wizard.estimated')}
                            />
                            <DesktopDatePicker
                                label={t('siteData.wizard.statementDate')}
                                inputFormat="YYYY-MM-DD"
                                value={values.statementDate ? dayjs(values.statementDate) : null}
                                onChange={value => setValue('statementDate', value?.isValid() ? value.format(ISO_DATE) : '')}
                                renderInput={params => <TextField {...params} size="small" sx={{ maxWidth: 220 }} />}
                            />
                        </Stack>
                    )
                    : (
                        <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    autoFocus
                                    label={t('siteData.column.produced')}
                                    size="small"
                                    value={values.amount}
                                    error={Number.isNaN(amount)}
                                    inputProps={{ inputMode: 'decimal' }}
                                    onChange={event => setValue('amount', event.target.value)}
                                    onKeyDown={nextOnEnter}
                                />
                                <TextField
                                    label={t('siteData.column.sold')}
                                    size="small"
                                    value={values.sold}
                                    error={Number.isNaN(sold)}
                                    inputProps={{ inputMode: 'decimal' }}
                                    onChange={event => setValue('sold', event.target.value)}
                                    onKeyDown={nextOnEnter}
                                />
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={OUTPUT_UNIT_SUGGESTIONS}
                                    value={outputUnit}
                                    onInputChange={(event, value) => setOutputUnit(value)}
                                    renderInput={params => <TextField {...params} label={t('siteData.column.unit')} />}
                                    sx={{ minWidth: 140 }}
                                />
                            </Stack>
                            <TextField
                                label={t('siteData.column.notes')}
                                size="small"
                                value={values.notes}
                                onChange={event => setValue('notes', event.target.value)}
                            />
                        </Stack>
                    );

            case 'review':
            default:
                return (
                    <Stack spacing={1.5}>
                        <ReviewLine label={t(isBill ? 'siteData.wizard.meter' : 'siteData.wizard.scope')}>
                            {isBill
                                ? `${t(`siteData.commodity.${meter?.commodity}`)} · ${meter?.utilityName} · ${meter?.accountNumber}`
                                : (locationWide ? t('siteData.production.locationWide') : t('siteData.wizard.thisSite'))}
                        </ReviewLine>
                        <ReviewLine label={t('siteData.wizard.period')}>
                            {period.periodStart} → {period.periodEnd} ({t('siteData.wizard.days', { count: days })})
                        </ReviewLine>
                        <ReviewLine label={isBill ? t('siteData.column.consumption') : t('siteData.column.produced')}>
                            {formatQuantity(amount)} {unitCode}
                            {isBill && values.isEstimated ? ` · ${t('siteData.column.estimated')}` : ''}
                        </ReviewLine>
                        {!isBill && sold !== null && (
                            <ReviewLine label={t('siteData.column.sold')}>{formatQuantity(sold)} {unitCode}</ReviewLine>
                        )}
                        {unusual && (
                            <Alert severity="warning" icon={<WarningAmberIcon />}>
                                {t('siteData.wizard.unusual', { percent: Math.round(deviation * 100) })}
                            </Alert>
                        )}
                    </Stack>
                );
        }
    };

    const nothingToEnter = isBill && billedMeters.length === 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t(isBill ? 'siteData.wizard.titleBill' : 'siteData.wizard.titleProduction')}</DialogTitle>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DialogContent dividers sx={{ minHeight: 280 }}>
                    {loading && <LinearProgress sx={{ mb: 2 }} />}
                    {nothingToEnter && <Alert severity="info">{t('siteData.wizard.noBilledMeters')}</Alert>}

                    {!nothingToEnter && !saved && (
                        <>
                            <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                                {steps.map(name => (
                                    <Step key={name}>
                                        <StepLabel>{t(`siteData.wizard.step.${name}`)}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            {!loading && renderStep()}
                        </>
                    )}

                    {saved && (
                        <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
                            <Typography>
                                {t('siteData.wizard.saved', { start: saved.periodStart, end: saved.periodEnd })}
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>
            </LocalizationProvider>
            <DialogActions>
                {saved
                    ? (
                        <>
                            <Button onClick={onClose}>{t('siteData.import.done')}</Button>
                            <Button variant="contained" onClick={enterNext}>{t('siteData.wizard.enterNext')}</Button>
                        </>
                    )
                    : (
                        <>
                            <Button onClick={onClose}>{t('common.cancel')}</Button>
                            <Box sx={{ flexGrow: 1 }} />
                            <Button onClick={back} disabled={step === 0 || saving}>{t('siteData.wizard.back')}</Button>
                            {steps[step] === 'review'
                                ? (
                                    <SaveButton variant="contained" loading={saving} onClick={save}>
                                        {t('siteData.action.save')}
                                    </SaveButton>
                                )
                                : (
                                    <Button variant="contained" disabled={!!stepError || loading || nothingToEnter} onClick={next}>
                                        {t('siteData.wizard.next')}
                                    </Button>
                                )}
                        </>
                    )}
            </DialogActions>
        </Dialog>
    );
}

function ReviewLine ({ label, children }) {
    return (
        <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}</Typography>
            <Typography variant="body2">{children}</Typography>
        </Stack>
    );
}

ReviewLine.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node,
};

EntryWizard.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /** 'bill' for a utility reading on one of the site's meters, 'production' for an output figure. */
    kind: PropTypes.oneOf(['bill', 'production']).isRequired,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    siteId: PropTypes.number,
    /** The site's active meters; only billed ones can be entered by hand. Bill wizard only. */
    meters: PropTypes.array,
    units: PropTypes.array,
    /** The meter to start on, such as the one the grid is showing. */
    initialMeterId: PropTypes.number,
    /** Called with each saved bill or production record, so the grid can show it. */
    onSaved: PropTypes.func.isRequired,
};
